import { rawSpacing } from "../styles/theme.css";

// SVG cannot measure text before it paints; width is approximated per character.
export const AXIS_CHAR_WIDTH = 7;
const AXIS_LABEL_GAP = rawSpacing[2];

/**
 * How many buckets apart printed labels have to sit to stop overlapping —
 * 1 prints every label, 2 every other one, and so on.
 */
export function getLabelStride(
    labels: readonly string[],
    bucketSpacing: number
): number {
    if (bucketSpacing <= 0) {
        return 1;
    }
    const labelWidth =
        Math.max(0, ...labels.map((label) => label.length)) * AXIS_CHAR_WIDTH +
        AXIS_LABEL_GAP;
    return Math.ceil(labelWidth / bucketSpacing);
}

/**
 * Every stride-th label, plus the anchors, which always print. A strided label
 * within a stride of an anchor is dropped, so the two never collide.
 */
export function buildAxisTickLabels(
    labels: readonly string[],
    stride: number,
    anchorIndexes: readonly (number | undefined)[] = []
): string[] {
    if (stride <= 1) {
        return [...labels];
    }

    const lastIndex = labels.length - 1;
    const anchors = anchorIndexes.filter(
        (index): index is number =>
            index !== undefined && index >= 0 && index <= lastIndex
    );
    const strided = Array.from(
        { length: Math.floor(lastIndex / stride) + 1 },
        (_, step) => step * stride
    ).filter((index) =>
        anchors.every((anchor) => Math.abs(anchor - index) >= stride)
    );

    return [...new Set([...strided, ...anchors])]
        .sort((left, right) => left - right)
        .map((index) => labels[index]);
}
